import { SHIPPER_SHORTCUTS } from 'constants/shippers';

const COMMON_REMOVE_FIELDS = [
    'id',
    'statuses',
    'user',
    'last_status',
    'cod_payment',
    'tracking_number',
    'shipper',
    'total_weight',
    'packages',
    'others',
];

/** Base fields for CSV import column mapping (receiver + reference). Overridable per shipper via csvBaseFields. */
const CSV_IMPORT_BASE_FIELDS = [
    { name: 'reference_number', required: true },
    { name: 'receiver_name', required: true },
    { name: 'receiver_company' },
    { name: 'receiver_street', required: true },
    { name: 'receiver_house_number' },
    { name: 'receiver_zip', required: true },
    { name: 'receiver_city', required: true },
    { name: 'receiver_state_code' },
    { name: 'receiver_phone', required: true },
    { name: 'receiver_email', required: true },
];

const SHIPPER_FIELD_DEFINITIONS = {
    [SHIPPER_SHORTCUTS.PACKETA]: {
        formFields: ({ id }) => [
            { name: 'reference_number', required: true },
            ...(id ? [] : [{ name: 'count_of_packages' }]),
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'insurance' },
            { name: 'weight', required: true },
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS],
    },
    [SHIPPER_SHORTCUTS.SPS]: {
        formFields: ({ isBasicVersion }) => [
            { name: 'reference_number', required: true },
            { name: 'count_of_packages' },
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'insurance' },
            ...(isBasicVersion ? [] : [{ name: 'reverse' }]),
            { name: 'save_address' },
        ],
        csvExtraFields: [
            { name: 'delivery_type' },
            { name: 'parcelshop', labelKey: 'parcelshop_id' },
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS, 'carrier_type', 'weight'],
    },
    [SHIPPER_SHORTCUTS.GLS]: {
        formFields: ({ isBasicVersion }) => [
            { name: 'reference_number', required: true },
            { name: 'count_of_packages' },
            { name: 'cod_reference' },
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'insurance' },
            ...(isBasicVersion ? [] : [{ name: 'reverse' }]),
            { name: 'save_address' },
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS, 'carrier_type', 'weight'],
    },
    [SHIPPER_SHORTCUTS.DPD]: {
        formFields: ({ isBasicVersion }) => [
            { name: 'reference_number', required: true },
            { name: 'count_of_packages' },
            { name: 'cod_reference' },
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'insurance' },
            { name: 'weight' },
            ...(isBasicVersion ? [] : [{ name: 'reverse' }]),
            { name: 'save_address' },
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS, 'carrier_type'],
    },
    [SHIPPER_SHORTCUTS.QDL]: {
        formFields: ({ isBasicVersion }) => [
            { name: 'reference_number', required: true },
            { name: 'count_of_packages' },
            { name: 'cod_reference' },
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'insurance' },
            ...(isBasicVersion ? [] : [{ name: 'reverse' }]),
            { name: 'save_address' },
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS, 'carrier_type', 'weight'],
    },
    [SHIPPER_SHORTCUTS.SKPOST]: {
        formFields: ({ isBasicVersion }) => [
            { name: 'reference_number', required: true },
            { name: 'count_of_packages' },
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'insurance' },
            { name: 'weight', required: true },
            ...(isBasicVersion ? [] : [{ name: 'reverse' }]),
            { name: 'save_address' },
            { name: 'others_reception_method' },
            ...(isBasicVersion ? [{ name: 'others_payment_type_code' }] : []),
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS.filter((f) => f !== 'others'), 'carrier_type'],
    },
    [SHIPPER_SHORTCUTS.SDS]: {
        formFields: [
            { name: 'reference_number', required: true },
            { name: 'count_of_packages' },
            { name: 'cod_reference' },
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'weight', required: true },
            { name: 'save_address' },
            { name: 'others_package_type' },
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS.filter((f) => f !== 'others'), 'carrier_type'],
    },
    'GLS_PARCELSHOP': {
        csvBaseFields: [
            { name: 'reference_number', required: true },
            { name: 'receiver_name', required: true },
            { name: 'receiver_phone', required: true },
            { name: 'receiver_email', required: true },
        ],
        formFields: ({ isBasicVersion }) => [
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'cod_reference' },
            { name: 'insurance' },
            { name: 'parcelshop' },
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS, 'carrier_type'],
    },
    default: {
        formFields: ({ isBasicVersion }) => [
            { name: 'reference_number', required: true },
            { name: 'cod_price' },
            { name: 'cod_currency_code' },
            { name: 'insurance' },
            ...(isBasicVersion ? [] : [{ name: 'reverse' }]),
            { name: 'save_address' },
        ],
        submitRemoveFields: [...COMMON_REMOVE_FIELDS, 'carrier_type', 'weight'],
    },
};

function getShipperDefinition(shortcut) {
    if (!shortcut) return SHIPPER_FIELD_DEFINITIONS.default;
    const key = Object.values(SHIPPER_SHORTCUTS).find(v => v.toUpperCase() === shortcut.toUpperCase()) || shortcut.toUpperCase();
    return SHIPPER_FIELD_DEFINITIONS[key] ?? SHIPPER_FIELD_DEFINITIONS.default;
}

export function isBasicShipperVersion(appValues, shipper) {
    if (shipper?.shortcut?.toUpperCase() === SHIPPER_SHORTCUTS.DPD) {
        return true;
    }

    const userShipperPriceList = appValues?.user?.user_shipper_price_lists?.find((uspl) => {
        if (shipper?.id) {
            return uspl.shipper.id === shipper.id;
        }
        return uspl.shipper.shortcut.toUpperCase() === shipper?.shortcut?.toUpperCase();
    });

    return userShipperPriceList?.sale_price_list?.type === 'fix';
}

const CONNECTOR_BULK_EXCLUDED_FIELDS = ['reverse'];

/**
 * Connector bulk import (PackageStep): fields from getCsvMappingFields with reference_number and excluded fields removed.
 */
export function getConnectorMappingFields(shipperShortcut) {
    const { base, additional } = getCsvMappingFields(shipperShortcut);
    const def = getShipperDefinition(shipperShortcut);
    const excluded = def.connectorBulkExcludedFields ?? CONNECTOR_BULK_EXCLUDED_FIELDS;
    return [...base, ...additional]
        .map((f) => f.name)
        .filter((name) => name !== 'reference_number' && !excluded.includes(name));
}

/**
 * CSV import: which columns can be mapped. base = receiver + reference_number; additional = shipper form fields not in base.
 * Use shipperShortcut e.g. 'GLS_PARCELSHOP' for parcelshop CSV import.
 */
export function getCsvMappingFields(shipperShortcut, options = {}) {
    const def = getShipperDefinition(shipperShortcut);
    const base = def.csvBaseFields ?? CSV_IMPORT_BASE_FIELDS;
    const baseNames = new Set(base.map((f) => f.name));
    const { isBasicVersion = true } = options;
    const rawForm =
        typeof def.formFields === 'function' ? def.formFields({ isBasicVersion, id: null }) : def.formFields;
    const additional = rawForm
        .filter((f) => !baseNames.has(f.name) && f.name !== 'save_address')
        .map((f) => ({ name: f.name, required: f.required }));
    for (const field of def.csvAdditionalFields ?? []) {
        if (!baseNames.has(field.name) && !additional.some((f) => f.name === field.name)) {
            additional.push({ name: field.name, required: field.required });
        }
    }
    const extra = def.csvExtraFields ?? [];
    return { base, additional, extra };
}

/**
 * For AdditionalnfoForm: per-shipper field specs consumed as:
 *   const specFn = SHIPPER_FIELD_SPECS[shipperShortcut] ?? SHIPPER_FIELD_SPECS.default;
 *   const { fields } = specFn({ isBasicVersion, id });
 *
 * Each field entry: { name: string, extraProps: object }
 */
export const SHIPPER_FIELD_SPECS = Object.fromEntries(
    Object.entries(SHIPPER_FIELD_DEFINITIONS).map(([key, def]) => [
        key,
        (ctx) => {
            const raw = typeof def.formFields === 'function' ? def.formFields(ctx) : def.formFields;
            return {
                fields: raw.map(({ name, required = false }) => ({
                    name,
                    extraProps: required !== false ? { required } : {},
                })),
            };
        },
    ])
);

/**
 * Removes redundant package fields and normalizes data.services by service definitions.
 * @param {object} data - Form payload (mutated)
 * @param {string} [shipperShortcut]
 * @param {Array<{ code: string, input_type?: string }>} services - Available service definitions
 */
export function removeRedundantFields(data, shipperShortcut, services = []) {
    for (const field of getShipperDefinition(shipperShortcut).submitRemoveFields) {
        delete data[field];
    }
    if (!data.services) return data;
    for (const key in data.services) {
        const serviceDefinition = services.find(s => s.code === key.replace('value', ''));
        if (!serviceDefinition) {
            delete data.services[key];
            delete data.services[key + 'value'];
        } else if (!serviceDefinition.input_type) {
            delete data.services[key + 'value'];
        }
    }
    return data;
}

/**
 * Package type options for SDS shipper
 */
export const SDS_PACKAGE_TYPE_OPTIONS = [
    { code: 'EP',  label: 'EP – Jednocestná paleta' },
    { code: 'BL',  label: 'BL – Balík' },
    { code: 'DP',  label: 'DP – Dvojpaleta' },
    { code: 'FP',  label: 'FP – Europaleta' },
    { code: 'KT',  label: 'KT – Kartón' },
    { code: 'KS',  label: 'KS – Kus' },
    { code: 'HP',  label: 'HP – Polpaleta' },
    { code: 'DL',  label: 'DL – Dodací list' },
    { code: 'FPN', label: 'FPN – Europaleta nadrozmerná' },
    { code: 'BLN', label: 'BLN – Balík nadrozmerný' },
    { code: 'EPN', label: 'EPN – Paleta nadrozmerná' },
    { code: 'RL',  label: 'RL – Rolka' },
    { code: 'OBL', label: 'OBL – Obálka' },
    { code: 'FA',  label: 'FA – Sud' },
    { code: 'BU',  label: 'BU – Zväzok' },
    { code: 'PN',  label: 'PN – Pneumatika' },
    { code: 'CL',  label: 'CL – Colli' },
];
